// =============================================================================
// BP-07-03: DiagnosisNav Component
// =============================================================================

import { useEffect, useState } from 'react';
import { DIAGNOSIS_SECTIONS, type DiagnosisSection } from '@/features/resources/types/resourcesTypes';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface DiagnosisNavProps {
  sections?: DiagnosisSection[];
  className?: string;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function DiagnosisNav({ sections = DIAGNOSIS_SECTIONS, className }: DiagnosisNavProps) {
  const [activeSection, setActiveSection] = useState<string>(sections[0]?.anchor || '');
  const [isSticky, setIsSticky] = useState(false);

  // Track scroll position to update active section
  useEffect(() => {
    const handleScroll = () => {
      // Check if nav should be sticky
      const scrollY = window.scrollY;
      setIsSticky(scrollY > 400);

      // Find the current section
      const sectionElements = sections
        .map((section) => ({
          id: section.anchor,
          element: document.getElementById(section.anchor),
        }))
        .filter((item) => item.element !== null);

      const scrollPosition = scrollY + 150; // Offset for header

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const { id, element } = sectionElements[i];
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (anchor: string) => {
    const element = document.getElementById(anchor);
    if (element) {
      const offset = 120; // Account for sticky header
      const top = element.offsetTop - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <nav
      className={cn(
        'transition-all duration-300',
        isSticky ? 'sticky top-20 z-40' : '',
        className
      )}
    >
      {/* Desktop Navigation - Horizontal */}
      <div className="hidden lg:block">
        <div
          className={cn(
            'bg-white rounded-xl border border-warm-gray/10 p-2',
            isSticky && 'shadow-soft'
          )}
        >
          <ul className="flex items-center gap-1">
            {sections.map((section) => (
              <li key={section.anchor}>
                <button
                  onClick={() => scrollToSection(section.anchor)}
                  className={cn(
                    'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                    activeSection === section.anchor
                      ? 'bg-sage text-white'
                      : 'text-navy hover:bg-cream-dark'
                  )}
                >
                  {section.title}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Mobile Navigation - Dropdown style */}
      <div className="lg:hidden">
        <div
          className={cn(
            'bg-white rounded-xl border border-warm-gray/10 p-3',
            isSticky && 'shadow-soft'
          )}
        >
          <select
            value={activeSection}
            onChange={(e) => scrollToSection(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-cream-dark border-0 text-navy font-medium focus:ring-2 focus:ring-sage/20"
          >
            {sections.map((section) => (
              <option key={section.anchor} value={section.anchor}>
                {section.title}
              </option>
            ))}
          </select>
        </div>
      </div>
    </nav>
  );
}

// -----------------------------------------------------------------------------
// DiagnosisNavSidebar - Vertical variant for sidebars
// -----------------------------------------------------------------------------

interface DiagnosisNavSidebarProps {
  sections?: DiagnosisSection[];
  activeSection?: string;
  className?: string;
}

export function DiagnosisNavSidebar({
  sections = DIAGNOSIS_SECTIONS,
  activeSection: controlledActive,
  className,
}: DiagnosisNavSidebarProps) {
  const [activeSection, setActiveSection] = useState<string>(
    controlledActive || sections[0]?.anchor || ''
  );

  // Track scroll position
  useEffect(() => {
    if (controlledActive !== undefined) {
      setActiveSection(controlledActive);
      return;
    }

    const handleScroll = () => {
      const sectionElements = sections
        .map((section) => ({
          id: section.anchor,
          element: document.getElementById(section.anchor),
        }))
        .filter((item) => item.element !== null);

      const scrollPosition = window.scrollY + 150;

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const { id, element } = sectionElements[i];
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections, controlledActive]);

  const scrollToSection = (anchor: string) => {
    const element = document.getElementById(anchor);
    if (element) {
      const offset = 120;
      const top = element.offsetTop - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <nav className={cn('sticky top-24', className)}>
      <div className="bg-white rounded-xl border border-warm-gray/10 p-4">
        <h3 className="font-display font-medium text-navy mb-4">On This Page</h3>
        <ul className="space-y-1">
          {sections.map((section) => (
            <li key={section.anchor}>
              <button
                onClick={() => scrollToSection(section.anchor)}
                className={cn(
                  'w-full text-left px-3 py-2 rounded-lg text-sm transition-all',
                  activeSection === section.anchor
                    ? 'bg-sage/10 text-sage-dark font-medium border-l-2 border-sage'
                    : 'text-warm-gray hover:text-navy hover:bg-cream-dark'
                )}
              >
                {section.title}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}

export default DiagnosisNav;

